"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useRef, useState } from "react";
import { Quote } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const testimonials = [{
  id: 1,
  quote: "La producción de Rolver Music elevó mi mensaje espiritual a un nivel profesional que nunca imaginé. Capturaron la esencia de mi fe en cada nota.",
  name: "Gabriel Méndez",
  role: "Cantautor Cristiano",
  avatarUrl: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689867/img/produccion_musical_gabriel.png",
  initials: "GM"
}, {
  id: 2,
  quote: "Creamos una canción personalizada para el aniversario de mis padres y fue el momento más emotivo de la noche. Un trabajo impecable y creativo.",
  name: "Elena Rodríguez",
  role: "Cliente Particular",
  avatarUrl: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689867/img/musica_personalizada_elena.png",
  initials: "ER"
}, {
  id: 3,
  quote: "Su capacidad para entender la visión de nuestra congregación y transformarla en música inspiradora es un verdadero don. Altamente recomendados.",
  name: "Pastor David Soto",
  role: "Líder de Alabanza",
  avatarUrl: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689867/img/ministerio_musica_david.png",
  initials: "DS"
}, {
  id: 4,
  quote: "Profesionalismo puro. Desde los arreglos hasta la mezcla final, Rolver Music Productions superó todas nuestras expectativas técnicas y artísticas.",
  name: "Ricardo Valles",
  role: "Productor de Eventos",
  avatarUrl: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689867/img/eventos_corporativos_ricardo.png",
  initials: "RV"
}, {
  id: 5,
  quote: "Buscábamos un sonido moderno pero con alma, y ellos lo lograron. Es inspirador trabajar con personas que comparten tus mismos valores y pasión.",
  name: "Sofía Martínez",
  role: "Artista Independiente",
  avatarUrl: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689867/img/artista_independiente_sofia.png",
  initials: "SM"
}];
export default function VocesDeGratitud() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: carouselRef,
    isVisible: isCarouselVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="235669" id="voces-de-gratitud" className="dark bg-background py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-12 md:mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default text-foreground mb-4 tracking-wider uppercase">
            Voces de Gratitud
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            Testimonios de quienes han confiado en nuestra creatividad para dar vida a sus proyectos musicales más significativos.
          </p>
        </div>

        <div ref={carouselRef} className={`relative px-4 sm:px-12 transition-all duration-1000 delay-200 ease-out ${isCarouselVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <Carousel opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4 md:-ml-6">
              {testimonials.map((testimonial, index) => <CarouselItem key={testimonial.id} data-index={index} className="pl-4 md:pl-6 md:basis-1/2 lg:basis-1/3">
                  <div className="h-full p-1">
                    <Card className="h-full flex flex-col border-border/20 shadow-lg hover:shadow-primary/10 transition-all duration-500 bg-card/50 backdrop-blur-xl rounded-lg">
                      <CardContent className="pt-8 flex-grow">
                        <Quote className="size-10 text-secondary/40 mb-6" aria-hidden="true" />
                        <p className="text-foreground/90 text-xl leading-relaxed italic font-serif">
                          "{testimonial.quote}"
                        </p>
                      </CardContent>
                      <CardFooter className="pb-8 pt-4 border-t border-border/10 mt-auto">
                        <div className="flex items-center gap-4">
                          <SafeHydrate><Avatar className="size-14 border-2 border-secondary shadow-md">
                            <AvatarImage src={testimonial.avatarUrl} alt={testimonial.name} />
                            <AvatarFallback className="bg-secondary/20 text-secondary font-default font-bold">
                              {testimonial.initials}
                            </AvatarFallback>
                          </Avatar></SafeHydrate>
                          <div>
                            <h4 className="font-default font-bold text-foreground tracking-wide">
                              {testimonial.name}
                            </h4>
                            <div className="flex flex-col">
                              <p className="text-sm text-muted-foreground font-default">
                                {testimonial.role}
                              </p>
                              <span className="text-[10px] uppercase tracking-widest text-secondary mt-1 font-default font-semibold">
                                Proyecto Verificado
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardFooter>
                    </Card>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
            <div className="hidden sm:block">
              <CarouselPrevious className="bg-background/50 border-secondary/30 hover:bg-secondary hover:text-secondary-foreground transition-all duration-300 hover:shadow-[0_0_15px_rgba(45,93,55,0.5)]" />
              <CarouselNext className="bg-background/50 border-secondary/30 hover:bg-secondary hover:text-secondary-foreground transition-all duration-300 hover:shadow-[0_0_15px_rgba(45,93,55,0.5)]" />
            </div>
          </Carousel>
        </div>
      </div>
    </section>;
}